import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import SortableTree from '@nosferatu500/react-sortable-tree';
import {
  changeNodeAtPath,
  defaultGetNodeKey,
  removeNodeAtPath,
} from '@nosferatu500/react-sortable-tree';

import { Message } from '@components';
import {
  FormFieldset,
  SelectField,
  TextField,
  ToggleField,
  DatamodelFormField,
  FormError,
} from '@Forms/fields.jsx';
import {
  slugifyUnderscore,
  urlFormat,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { nonRenderingPageTypes } from '@constants';
import { ButtonSpinnerIcon, Edit2Icon, TrashIcon } from '@svgs';
import { useDatamodel } from '@hooks';
import get from 'lodash/get';

import '@nosferatu500/react-sortable-tree/style.css'; // This only needs to be imported once in your app
import './MenuManager.scss';

const traverseLink = (link) => {
  // traverse the link and its children and remove IDs and order
  delete link.depth;
  delete link.id;
  delete link.order;
  delete link.parent_id;
  if (link.children && link.children.length) {
    link.children = link.children.map((l) => traverseLink(l));
  }
  return link;
};

const linklistToTreeData = (links) => {
  const treeData = [];
  links.forEach((link) => {
    treeData.push(traverseLink(link));
  });
  return treeData;
};

const NodeFields = ({
  control,
  errors,
  getValues,
  pagesData,
  register,
  setValue,
  watch,
}) => {
  // shared fields across add and edit views

  // state used based on the currently selected value of the link type
  const [linkType, setLinkType] = useState(null);
  const [autoAddSubpages, setAutoAddSubpages] = useState(false);

  useEffect(() => {
    // set the link type based on the current type
    setLinkType(getValues(`link_type`));
  });

  // keep auto-add toggle in sync with form state when editing existing links
  const addMatchingSubpages = watch('add_matching_subpages');
  useEffect(() => {
    setAutoAddSubpages(!!addMatchingSubpages);
  }, [addMatchingSubpages]);

  // change event on link type
  const linkTypeChange = (e) => {
    setLinkType(e.target.value);

    if (!['page'].includes(e.target.value)) {
      // disable page_id association if not a page type
      setValue('page_id', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (!['url'].includes(e.target.value)) {
      // remove url value if not a URL
      setValue('url', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const pageChange = (e) => {
    // add the page name to the dataset which is used for
    // display and the placeholder for link text
    const page = pagesData.objects.find(
      (p) => p.id === parseInt(e.target.value),
    );
    setValue(`name`, page.name);
  };

  // pages choices for dropdown
  let pagesChoices = pagesData.objects
    // exclude non-rending page types
    .filter((p) => !nonRenderingPageTypes.includes(p.type))
    .map((p) => {
      return (
        <option key={p.id} value={p.id}>
          {p.path}
        </option>
      );
    });

  return (
    <>
      <input type="hidden" {...register('name')} />
      <SelectField
        register={register}
        error={errors.link_type}
        options={{
          required: 'This field is required',
          onChange: linkTypeChange,
        }}
        name="link_type"
        label="Link type"
      >
        <option value="">Choose a type...</option>
        <option value="page">Page link</option>
        <option value="url">URL / Link</option>
        <option value="blank">Blank</option>
      </SelectField>
      {['blank'].includes(linkType) && (
        <TextField
          register={register}
          error={errors.name_override}
          options={{ required: 'This field is required' }}
          name={`name_override`}
          label="Link text"
          helpText="Text used for the link"
        />
      )}
      {['page'].includes(linkType) && (
        <>
          <SelectField
            register={register}
            error={errors.page_id}
            options={{
              required: 'This field is required',
              onChange: pageChange,
            }}
            name="page_id"
            label="Page"
          >
            <option value="">Select page to link to...</option>
            {pagesChoices}
          </SelectField>
          <TextField
            register={register}
            error={errors.name_override}
            name={`name_override`}
            label="Link text override"
            placeholder={watch('name')}
            helpText="Modify the page name with a different value in the menu"
          />
        </>
      )}
      {['url'].includes(linkType) && (
        <>
          <TextField
            register={register}
            error={errors.name_override}
            options={{ required: 'This field is required' }}
            name={`name_override`}
            label="Link text"
            helpText="Text used for the link"
          />
          <TextField
            register={register}
            error={errors.url}
            options={{
              required: 'This field is required',
              onChange: urlFormat,
            }}
            name="url"
            label="URL to link to"
            placeholder="/your-page/"
            helpText="Can be an internal link (/some-page/) or an external link (https://www.example.com/...)"
          />
          <ToggleField
            register={register}
            control={control}
            error={errors.link_matching_page}
            name="link_matching_page"
            label="Auto-link to matching page"
            helpText="If a local page exists at this path, link to the local version instead"
            onChange={(e) => {
              if (!e.target.checked) {
                setValue('link_matching_page_opt_in', false, {
                  shouldDirty: true,
                });
              }
            }}
          />
          {watch('link_matching_page') && (
            <ToggleField
              register={register}
              control={control}
              error={errors.link_matching_page_opt_in}
              name="link_matching_page_opt_in"
              label="Link matching page opt-in"
              helpText="If Auto-link to matching page does not find a match, consider this link as 'opt-out' within the menu"
            />
          )}
        </>
      )}
      {linkType && (
        <TextField
          register={register}
          options={{
            onChange: slugifyUnderscore,
            onBlur: (e) => {
              slugifyOnBlur(e, '_');
              setValue('custom_field_opt_in', e.target.value, {
                shouldDirty: true,
                shouldValidate: true,
              });
            },
          }}
          error={errors.custom_field_opt_in}
          name="custom_field_opt_in"
          label="Opt-in from custom field"
          helpText="Reference to the Site custom field (i.e. custom_field_name) that determines if this link should be concidered 'opt-out' within the menu"
        />
      )}
      {['url'].includes(linkType) &&
        (watch('link_matching_page_opt_in') ||
          watch('custom_field_opt_in')) && (
          <SelectField
            register={register}
            options={{}}
            name="opt_out_behavior"
            label="Opt out behavior"
            helpText="Determines what happens to a link if it is 'opt-out' from theopt in settings above."
          >
            <option value="hide" defaultChecked>
              Hide the link from menu
            </option>
            <option value="blank">Change to a blank link </option>
          </SelectField>
        )}
      {['page', 'url', 'blank'].includes(linkType) && (
        <>
          <ToggleField
            register={register}
            control={control}
            error={errors.add_matching_subpages}
            name="add_matching_subpages"
            label="Automatically add sub-pages"
            helpText="Automatically add any pages under this path as links"
            onChange={(e) => {
              const checked = e.target.checked;
              setAutoAddSubpages(checked);
              setValue('add_matching_subpages', checked);
              if (!checked) {
                // clear stale parent path when auto add is disabled
                setValue('add_matching_subpages_path', '');
              }
            }}
          />
          {autoAddSubpages && ['blank'].includes(linkType) && (
            <TextField
              register={register}
              error={errors.add_matching_subpages_path}
              options={{
                required: 'This field is required',
                onChange: urlFormat,
              }}
              name="add_matching_subpages_path"
              label="Subpages parent path"
              placeholder="/parent-path/"
              helpText="Parent path used for the automatically add sub-pages option"
            />
          )}
        </>
      )}
    </>
  );
};

const AddNodeForm = ({ onSubmit, pagesData }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    reset,
    setError,
    setFocus,
    setValue,
    watch,
  } = useForm({
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  const onSubmitWrapper = (data) => {
    onSubmit(data);
    reset({
      link_type: '',
      name: '',
      name_override: '',
      page_id: null,
      url: '',
      add_matching_subpages_path: '',
      add_matching_subpages: false,
      link_matching_page: false,
      link_matching_page_opt_in: false,
      custom_field_opt_in: '',
      opt_out_behavior: 'hide',
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitWrapper)}
      className={`sb-manage-content-form`}
      style={{ width: '50%' }}
    >
      <FormFieldset legend="Add a new link">
        <NodeFields
          control={control}
          register={register}
          errors={errors}
          getValues={getValues}
          watch={watch}
          pagesData={pagesData}
          setValue={setValue}
          reset={reset}
        />
      </FormFieldset>
      <button type="submit" className="sb-btn sb-btn-save">
        Add
      </button>
    </form>
  );
};

const EditNodeForm = ({
  node,
  onCancel,
  onSubmit,
  pagesData,
  datamodelData,
  relatedDatamodels,
  siteData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    reset,
    setError,
    setFocus,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: node,
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  const fieldsList = datamodelData?.fields?.map((field) => {
    const fieldName = `custom_fields.${field.slug}`;
    const fieldError = get(errors, fieldName);
    return (
      <div key={field.id} className="sb-menu-custom-field">
        <DatamodelFormField
          datamodelField={field}
          fieldPrefix={`custom_fields.`}
          relatedDatamodels={relatedDatamodels}
          siteID={siteData.id}
          useFormReturn={{
            register,
            formState: { errors },
            control,
            setValue,
            watch,
            getValues,
            handleSubmit,
            clearErrors,
            setError,
          }}
        />
        {field.field_type === 'related_model_list' && fieldError?.message && (
          <FormError message={fieldError.message} />
        )}
      </div>
    );
  });

  return (
    <form
      id="menu-manager-edit-form"
      onSubmit={handleSubmit(onSubmit)}
      className={`sb-manage-content-form`}
      style={{ width: '50%' }}
    >
      <FormFieldset legend="Editing link">
        <NodeFields
          control={control}
          register={register}
          errors={errors}
          getValues={getValues}
          watch={watch}
          pagesData={pagesData}
          setValue={setValue}
          reset={reset}
        />
      </FormFieldset>
      {!!fieldsList?.length && (
        <FormFieldset legend="Custom fields">{fieldsList}</FormFieldset>
      )}
    </form>
  );
};

const MenuItem = ({
  currentlyEditing,
  node,
  onDelete,
  path,
  setCurrentlyEditing,
  setCanDrag,
  setEditedNode,
}) => {
  return (
    <div>
      {node.name_override || node.name}
      {!currentlyEditing && (
        <>
          <button
            type="button"
            className="sb-btn"
            onClick={() => {
              setCanDrag(false);
              setCurrentlyEditing(path);
              setEditedNode(node);
            }}
          >
            <Edit2Icon />
          </button>
          <button type="button" className="sb-btn" onClick={onDelete}>
            <TrashIcon />
          </button>
        </>
      )}
    </div>
  );
};

export const MenuManager = ({
  disabled,
  linklistData,
  mutation,
  pagesData,
  siteData,
}) => {
  const [treeData, setTreeData] = useState([]);
  const [canDrag, setCanDrag] = useState(true);
  const [currentlyEditing, setCurrentlyEditing] = useState(false);
  const [editedNode, setEditedNode] = useState(false);

  const getNodeKey = defaultGetNodeKey;

  useEffect(() => {
    setTreeData(linklistToTreeData(linklistData.links));
  }, [linklistData]);

  console.log('linklistData', linklistData);

  const {
    datamodelData,
    isPending: isCustomMenuItemsPending,
    relatedDatamodels,
    relatedDatamodelsReady,
  } = useDatamodel('menu_item_custom_fields');

  const onSubmit = (e) => {
    e?.preventDefault();
    // save the whole linklist but sending the new links
    const data = {
      ...linklistData,
      links: treeData,
    };
    mutation.mutate(data);
  };

  const onAddSubmit = (data) => {
    // link editor - on adding a new link
    console.log('onAddSubmit', data);
    const dataWithDefaults = {
      ...data,
      page_id: data.page_id ? parseInt(data.page_id) : null,
      list_id: linklistData.id,
    };
    setTreeData(treeData.concat(dataWithDefaults));
  };

  const onEditSubmit = (data) => {
    console.log('onEditSubmit', data);
    console.log('editedNode', editedNode);
    // link editor - on editing a link
    setTreeData(
      // update node values
      changeNodeAtPath({
        treeData,
        path: currentlyEditing,
        getNodeKey,
        // preserve non-form properties like children when editing
        newNode: { ...editedNode, ...data },
      }),
    );
    setEditedNode(false);
    setCanDrag(true);
    setCurrentlyEditing(false);
  };
  const onEditCancel = () => {
    // link editor - canceling an edit
    setEditedNode(false);
    setCanDrag(true);
    setCurrentlyEditing(false);
  };

  return (
    <>
      <div className={`sb-flex ` + (disabled ? 'sb-menu-disabled' : '')}>
        {currentlyEditing && (
          <EditNodeForm
            node={editedNode}
            onSubmit={onEditSubmit}
            onCancel={onEditCancel}
            pagesData={pagesData}
            datamodelData={datamodelData}
            relatedDatamodels={relatedDatamodels}
            siteData={siteData}
          />
        )}
        {!currentlyEditing && (
          <AddNodeForm onSubmit={onAddSubmit} pagesData={pagesData} />
        )}
        <div
          style={{
            height: '600px',
            width: '50%',
            opacity: currentlyEditing ? 0.25 : 1,
          }}
        >
          <SortableTree
            treeData={treeData}
            canDrag={canDrag}
            maxDepth={4}
            generateNodeProps={({ node, path }) => ({
              // hide buttons
              buttons: [],
              // all display handled by a component
              title: (
                <MenuItem
                  node={node}
                  path={path}
                  currentlyEditing={currentlyEditing}
                  setCurrentlyEditing={setCurrentlyEditing}
                  setEditedNode={setEditedNode}
                  setCanDrag={setCanDrag}
                  onDelete={() => {
                    if (
                      window.confirm('Are you sure to remove this menu link?')
                    ) {
                      setTreeData(
                        removeNodeAtPath({
                          treeData,
                          path,
                          getNodeKey,
                        }),
                      );
                    }
                  }}
                />
              ),
            })}
            onChange={(d) => {
              // if tree is modified by dragging
              console.log('SortableTree', d);
              setTreeData(d);
            }}
          />
        </div>
      </div>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          {editedNode && (
            <>
              <button
                type="submit"
                form="menu-manager-edit-form"
                className="sb-btn sb-btn-save"
              >
                Update link
              </button>
              <button
                type="button"
                className="sb-btn sb-btn-cancel"
                onClick={onEditCancel}
              >
                Cancel
              </button>
            </>
          )}
          {!editedNode && (
            <button
              type="button"
              className="sb-btn sb-btn-save"
              disabled={disabled}
              onClick={onSubmit}
            >
              {mutation.isPending ? 'Publishing...' : 'Publish menu changes'}
              {mutation.isPending && <ButtonSpinnerIcon />}
            </button>
          )}
        </div>
        {mutation.isError && (
          <Message messageStyle="error" title="Error publishing changes" />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully published" />
        )}
      </div>
    </>
  );
};
